import React, { useEffect, useState } from "react";
import { Modal, ModalDialog } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { ConsensusActivityRender } from "components/pages/Session/Activity/activities/ConsensusActivity/ConsensusActivityRender";
import { buildPreviewChallenge } from "utils/challenge";
import { fetchConsensusQuestionsPage, CONSENSUS_PAGE_SIZE } from "utils/consensusQuestions";
import { request } from "hooks/useOkoApi/useOkoApi";
import {
  buildLessonQuestionsQuery,
  toQuestionsArray,
  getQuestionSet,
  fetchAllScopedQuestions,
} from "hooks/useCurriculum/useCurriculum";
import { EMPTY_QUESTION_FILTER, fromQuestionFilter, serializeQuestionFilter } from "components/pages/LessonsStandards/questionFilters";
import { QuestionFilterPanel } from "components/pages/LessonsStandards/QuestionFilterPanel";
import { useQuestionFacets, useStandardQuestionFacets } from "hooks/useQuestionFacets/useQuestionFacets";
import { colors, logEducatorDashboard, LogLevel } from "utils";
import { trackMeta } from "./AssignmentGroupCard";
import closeIcon from "assets/icons/review-student-results/close.svg";
import checkboxCheckedIcon from "assets/icons/review-student-results/checkbox-selected.svg";
import checkboxUncheckedIcon from "assets/icons/review-student-results/checkbox-selected-without-tick.svg";
import chevronLeftIcon from "assets/icons/review-student-results/chevron-left.svg";

// Header bar: centered nav controls + bordered close button (Figma 3665:32263).
// The left padding balances the 48px close button so the controls stay centered.
const HeaderBar = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding-left: 48px;
  width: 100%;
`;

const Controls = styled.div`
  align-items: center;
  display: flex;
  flex: 1 0 0;
  gap: 16px;
  justify-content: center;
`;

// The Student Results frame, handed to react-bootstrap as its own `dialogAs`
// so no other mode's dialog is touched. green/400 has no token in `colors`.
// 1004 wide outside, so the frame's 1000 sits inside the 2px border.
const FRAME_BORDER_GREEN_400 = "#858B87";

const StudentResultsDialog = styled(ModalDialog)`
  &.modal-dialog {
    --bs-modal-width: 1004px;
  }

  & .modal-content {
    border: 2px solid ${FRAME_BORDER_GREEN_400};
    border-radius: 16px;
    font-family: "Inter", sans-serif;
    overflow: hidden;
  }
`;

const RESULTS_BODY_STYLE = {
  background: colors.milkGlass500,
  display: "flex",
  flexDirection: "column",
  gap: 24,
  height: 656,
  overflowY: "auto",
  padding: 32,
};

// A spacer as wide as Show Answers keeps the carousel centred in the frame.
const RESULTS_SIDE_WIDTH = 147;

const ResultsControlRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const ResultsControlSide = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  min-height: 40px;
  width: ${RESULTS_SIDE_WIDTH}px;
`;

// The native checkbox is kept, visually hidden, so the icon stays a real,
// keyboard-operable checkbox named by its own label.
const ShowAnswersInput = styled.input`
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;

  &:focus-visible + label {
    outline: 2px solid ${colors.blue500};
    outline-offset: 2px;
  }
`;

const ShowAnswersLabel = styled.label`
  align-items: center;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
  display: flex;
  font-size: 16px;
  font-weight: 400;
  gap: 8px;
  letter-spacing: 0.25px;
  line-height: 22px;
  margin: 0;
  white-space: nowrap;
`;

const SHOW_ANSWERS_ID = "vqm-show-answers";

const UNAVAILABLE_QUESTION = "This question could not be loaded.";

// Standard, student and chip, beside the carousel's own controls. Quiet: it
// says which question of whose row this is, and the question is the content.
const OverlayCaption = styled.div`
  color: ${colors.nightlyWoods500};
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
`;

// Circular nav buttons — previous and next share the same active style; the
// bounds are conveyed by the disabled state, not by a permanently muted arrow.
const NavButton = styled.button`
  align-items: center;
  background: ${colors.nightlyWoods500};
  border: none;
  border-radius: 200px;
  color: white;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  padding: 8px;
  width: 32px;
  &:disabled {
    background: ${colors.gray700};
    cursor: not-allowed;
  }
`;

const Counter = styled.div`
  color: ${colors.nightlyWoods500};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  white-space: nowrap;
`;

const CloseButton = styled.button`
  align-items: center;
  background: transparent;
  border: 2px solid ${(p) => (p.$onDark ? "#fff" : colors.nightlyWoods950)};
  border-radius: 100px;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 48px;
  justify-content: center;
  padding: 0;
  width: 48px;
`;

// Question renders from the top down (align-items: flex-start) and is
// horizontally centered; the scrolling lives on Modal.Body.
// Same clamp as .eqc-question in exampleQuestionsCarousel.css: prompts are arbitrary
// backend HTML whose fixed-width tables would otherwise escape the question card.
const QuestionStage = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: center;
  width: 100%;

  > * {
    min-width: 0;
  }

  .prompt {
    width: 100%;
  }

  .prompt table {
    max-width: 100%;
  }

  .prompt td,
  .prompt th {
    width: auto !important;
    word-break: break-word;
  }

  .prompt img {
    max-width: 100%;
    height: auto;
  }
`;

const StateMessage = styled.div`
  color: ${colors.milkGlass900};
  padding: 32px;
  text-align: center;
`;

// Editable-mode chrome (Figma 5979-63695): dark green title band, white Group Code /
// Group Focus strip, then a grey content area with the filters and nav pinned above
// the scrolling question body — all inside a fixed 822px frame (dashboard.css).
const ModalTitle = styled.h2`
  color: ${colors.milkGlass500};
  font-family: "ABC Marfa", sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const InfoStrip = styled.div`
  background: white;
  color: ${colors.nightlyWoods500};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  font-size: 16px;
  gap: 8px;
  letter-spacing: 0.25px;
  line-height: 22px;
  padding: 16px;
`;

const InfoRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const InfoLabel = styled.span`
  font-weight: 700;
`;

// The same chip the group card draws, keyed by track.
const TrackTag = styled.span`
  background: ${(p) => p.$meta.tagBg};
  border: 1px solid ${(p) => p.$meta.tagBorder};
  border-radius: 11px;
  color: ${(p) => p.$meta.tagText};
  font-size: 14px;
  line-height: 20px;
  padding: 2px 8px;
  white-space: nowrap;
`;

const EditToolbar = styled.div`
  background: ${colors.milkGlass500};
  flex-shrink: 0;
  padding: 24px 32px 0;
`;

const EditNavRow = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 24px;
`;

const FooterButton = styled.button`
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
  font-weight: 700;
  height: 48px;
  line-height: 26px;
  padding: 0 20px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CancelButton = styled(FooterButton)`
  background: #fff;
  border: 1px solid ${colors.blue500};
  color: ${colors.blue500};
`;

const SaveButton = styled(FooterButton)`
  background: ${colors.blue500};
  border: none;
  color: ${colors.milkGlass500};
`;

const SaveError = styled.p`
  color: crimson;
  flex: 1;
  font-size: 14px;
  margin: 0;
`;

// "Create My Question Set" (Figma 5191-100622): text-only CTA at the right end
// of the filter row, jumping into the Question Set view.
const CreateQuestionSetButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${colors.blue700};
  cursor: pointer;
  display: inline-flex;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 400;
  gap: 4px;
  height: 48px;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 16px 0 8px;
  white-space: nowrap;

  &:hover { text-decoration: underline; }
  &:focus-visible {
    outline: 2px solid ${colors.blue500};
    outline-offset: 2px;
  }
`;

// Primary CTA of the saved-set preview (Figma 5127-62636).
const EditSetButton = styled.button`
  align-items: center;
  background: ${colors.blue500};
  border: none;
  border-radius: 8px;
  color: ${colors.milkGlass500};
  cursor: pointer;
  display: inline-flex;
  font-size: 18px;
  font-weight: 700;
  gap: 8px;
  height: 48px;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 16px 0 8px;
`;

const PencilIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 5L19 9M21.1739 6.81189C21.7026 6.28332 21.9997 5.56636 21.9998 4.81875C21.9999 4.07113 21.703 3.3541 21.1744 2.82539C20.6459 2.29668 19.9289 1.99961 19.1813 1.99951C18.4337 1.99942 17.7166 2.29632 17.1879 2.82489L3.84193 16.1739C3.60975 16.4054 3.43805 16.6904 3.34193 17.0039L2.02093 21.3559C1.99509 21.4424 1.99314 21.5342 2.01529 21.6217C2.03743 21.7092 2.08285 21.7891 2.14673 21.8529C2.21061 21.9167 2.29055 21.962 2.37809 21.984C2.46563 22.006 2.55749 22.0039 2.64393 21.9779L6.99693 20.6579C7.3101 20.5626 7.59511 20.392 7.82693 20.1609L21.1739 6.81189Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CirclePlusIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M8 12h8" />
    <path d="M12 8v8" />
  </svg>
);

// `questionFilter` (optional) — the group's stored Advanced Selection filter, in its
// persisted snake_case dict form. When it narrows the pool, questions come
// from the matching FILTERED route so the preview shows exactly the set a session would
// draw from; otherwise the legacy unfiltered consensus-page browse is kept.
//
// Scope (mirrors QuestionFilterView): pass `lessonId` for lesson-bundle rows — the
// fetch uses curriculum/lessons/{id}/questions, with `lessonStandardCode` pinning a
// single prereq/extension standard (null/omitted for Core rows, which span the
// lesson's addressing set narrowed by the filter's own `standards` subset). Without
// `lessonId`, a narrowing filter uses the standard-scoped route
// (curriculum/standards/{code}/questions).
export const ViewQuestionsModal = ({
  isOpen,
  onClose,
  standardCode,
  standardName,
  questionFilter = null,
  lessonId = null,
  lessonStandardCode = null,
  // Editable mode (Figma "Preview & Edit Question Bank"): the stored filter seeds a
  // draft the teacher can adjust; the carousel follows the draft, and Save hands the
  // serialized filter (null = no-op) back to the host to persist.
  editable = false,
  onSave = null,
  saving = false,
  saveError = null,
  // Renders the "Create My Question Set" CTA in the filter row (editable mode);
  // the host navigates to the Question Set view.
  onCreateQuestionSet = null,
  // Saved-set preview (Figma 5127-62636): when the group points at a QuestionSet,
  // the modal shows exactly that set's questions — no filters, no Save — with a
  // single "Edit My Question Set" CTA into the Question Set view.
  questionSetId = null,
  // Explicit-list mode: when `questions` is an array the modal fetches nothing
  // and the carousel walks the host's list. Student Reports chips carry
  // SessionReplayQuestion row ids, not question-bank ids, so there is no bank
  // page to browse. An entry may carry `student_responses`, one student's
  // graded answer, handed straight to the renderer.
  questions: explicitQuestions = null,
  initialIndex = 0,
  // Whose answers these are, named in the caption.
  studentName = null,
  // Group Code / Group Focus strip under the title band. `focus` is the host card's
  // resolveGroupCardTitle result ({ code, title, trackType }), so the modal names
  // the group exactly as the card it was opened from does. The track tag always
  // shows here (unlike the card, which hides it on standard-flow rows).
  groupCode = null,
  focus = null,
  // The "Review Student Results" variant (Figma 4729:72167). Meaningful only
  // with `questions`; without it the modal renders exactly as before.
  studentResults = false,
  // Told the shown position of an explicit list, so a host that fills the
  // list lazily knows what to fetch next.
  onIndexChange = null,
}) => {
  const isExplicitList = Array.isArray(explicitQuestions);

  const [fetchedQuestions, setFetchedQuestions] = useState([]);
  const [fetchedTotal, setFetchedTotal] = useState(0);
  // An explicit list opens where it was pointed from its first render, so a
  // host is never told about a position the teacher did not open.
  const [index, setIndex] = useState(() =>
    isExplicitList ? Math.min(Math.max(initialIndex, 0), Math.max(explicitQuestions.length - 1, 0)) : 0,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  // Show Answers opens checked, and the choice survives Previous and Next.
  // Reset on closing so the next opening's first frame is already checked.
  const [showAnswers, setShowAnswers] = useState(true);
  useEffect(() => {
    if (!isOpen) setShowAnswers(true);
  }, [isOpen]);

  // Serialize so effects key on the stored filter's values, not object identity.
  const storedFilterKey = JSON.stringify(questionFilter || null);

  const [draft, setDraft] = useState(EMPTY_QUESTION_FILTER);
  // Re-seed the draft from the stored filter each time the modal opens (or the
  // stored filter itself changes underneath an open modal).
  useEffect(() => {
    if (isOpen) setDraft(fromQuestionFilter(questionFilter) || EMPTY_QUESTION_FILTER);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, storedFilterKey]);

  // The filter the carousel follows: the live draft when editing, else the stored one.
  const activeQuestionFilter = editable ? serializeQuestionFilter(draft) : questionFilter;

  // Facets feed the editable dropdowns (counts + dead-option disabling), scoped the
  // same way as the question fetch below: lesson route when lessonId is set, else the
  // bare standard route. Disabled entirely (null id) in read-only mode or when closed.
  const showStandards = editable && Boolean(lessonId) && !lessonStandardCode;
  const facetsQuery = {
    standardCode: lessonStandardCode || undefined,
    standards: showStandards ? draft.standards : undefined,
    learningComponents: draft.learningComponents,
    questionType: draft.questionType,
  };
  // No facets for a saved set: it renders no filter row.
  const wantsFacets = editable && isOpen && !questionSetId;
  const lessonFacetsState = useQuestionFacets(wantsFacets && lessonId ? lessonId : null, facetsQuery);
  const standardFacetsState = useStandardQuestionFacets(
    wantsFacets && !lessonId ? standardCode : null,
    facetsQuery,
  );
  const facetsState = lessonId ? lessonFacetsState : standardFacetsState;

  // One page fetcher for both the initial load and load-more, so the filtered and
  // unfiltered paths can never disagree between pages. Returns { questions, total }.
  // Route choice keys on the POOL-NARROWING dimensions only (standards / learning
  // components / question type) — auto_assign_early_finishers governs exhaustion
  // behavior, not which questions are in the pool, so it alone doesn't switch routes.
  const hydratedFilter = fromQuestionFilter(activeQuestionFilter);
  const hasPoolNarrowing = Boolean(
    hydratedFilter &&
      (hydratedFilter.standards?.length ||
        hydratedFilter.learningComponents?.length ||
        (hydratedFilter.questionType && hydratedFilter.questionType !== "both")),
  );
  const fetchPage = async (offset) => {
    // Saved set: the carousel is the set itself, in stored position order. Page 0
    // resolves the whole thing (sets are small) by joining the set's refs against
    // the group's unfiltered bank; there is no page 2.
    if (questionSetId) {
      if (offset > 0) return { questions: [], total: 0 };
      const set = await getQuestionSet(questionSetId);
      const refs = [...(set?.questions || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      const { questions: bank } = await fetchAllScopedQuestions({
        lessonId,
        standardCode,
        filter: { standardCode: lessonStandardCode || undefined },
      });
      const byId = new Map(bank.map((q) => [q.id, q]));
      const resolved = refs.map((r) => byId.get(r.challenge_id)).filter(Boolean);
      return { questions: resolved, total: resolved.length };
    }
    if (hasPoolNarrowing && lessonId) {
      // Lesson-bundle rows: same query the edit view's filter panel runs, so the
      // preview count always agrees with it. Core spans the addressing set narrowed
      // by the stored `standards` subset; a pinned sub-item drops the subset (its
      // scope is already one standard), matching QuestionFilterView.
      const qs = buildLessonQuestionsQuery({
        limit: CONSENSUS_PAGE_SIZE,
        offset,
        filter: {
          standardCode: lessonStandardCode || undefined,
          standards: lessonStandardCode ? undefined : hydratedFilter.standards,
          learningComponents: hydratedFilter.learningComponents,
          questionType: hydratedFilter.questionType,
        },
      });
      const data = await request(`curriculum/lessons/${lessonId}/questions?${qs}`, { method: "GET" });
      return { questions: toQuestionsArray(data), total: Number(data?.total) || 0 };
    }
    if (hasPoolNarrowing && standardCode) {
      const qs = buildLessonQuestionsQuery({
        limit: CONSENSUS_PAGE_SIZE,
        offset,
        filter: {
          standards: hydratedFilter.standards,
          learningComponents: hydratedFilter.learningComponents,
          questionType: hydratedFilter.questionType,
        },
      });
      const data = await request(`curriculum/standards/${standardCode}/questions?${qs}`, { method: "GET" });
      return { questions: toQuestionsArray(data), total: Number(data?.total) || 0 };
    }
    return fetchConsensusQuestionsPage({ prefix: standardCode, offset });
  };

  // Keys on the ACTIVE filter, so draft edits refetch the carousel from page 0.
  // The set id joins the key so switching cards re-resolves the right set.
  const filterKey = JSON.stringify([activeQuestionFilter || null, questionSetId]);
  useEffect(() => {
    // An explicit list is already resolved, so no route is ever reached.
    if (isExplicitList || !isOpen || !standardCode) {
      return undefined;
    }
    let cancelled = false;
    const fetchQuestions = async () => {
      setIsLoading(true);
      setError(null);
      setFetchedQuestions([]);
      setFetchedTotal(0);
      setIndex(0);
      try {
        const { questions: pageQuestions, total: pageTotal } = await fetchPage(0);
        if (!cancelled) {
          setFetchedQuestions(pageQuestions);
          setFetchedTotal(pageTotal);
        }
      } catch (e) {
        logEducatorDashboard("ViewQuestionsModal: failed to load questions", {
          level: LogLevel.WARNING,
          event: "load_error",
          statusCode: e?.status,
          standardCode,
        });
        if (!cancelled) {
          setError(e?.detail || e?.message || "Failed to load questions.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    fetchQuestions();
    return () => {
      cancelled = true;
    };
    // fetchPage is rebuilt each render from (standardCode, lessonId,
    // lessonStandardCode, filterKey) — keying on those.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExplicitList, isOpen, standardCode, lessonId, lessonStandardCode, filterKey]);

  // `total` is the envelope for a fetched stream (pages may still come) and
  // the length for an explicit list.
  const questions = isExplicitList ? explicitQuestions : fetchedQuestions;
  const total = isExplicitList ? explicitQuestions.length : fetchedTotal;

  // Open on the chip that was clicked, not on the row's first.
  useEffect(() => {
    if (!isExplicitList) return;
    setIndex(Math.min(Math.max(initialIndex, 0), Math.max(explicitQuestions.length - 1, 0)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExplicitList, isOpen, initialIndex, explicitQuestions?.length]);

  useEffect(() => {
    if (isExplicitList && onIndexChange) onIndexChange(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExplicitList, index]);

  const handlePrev = () => setIndex((i) => Math.max(0, i - 1));
  const handleNext = async () => {
    // Advance within the loaded set; at its end, fetch the next page then step.
    if (index < questions.length - 1) {
      setIndex((i) => i + 1);
      return;
    }
    if (isLoadingMore || questions.length >= total) return;
    setIsLoadingMore(true);
    try {
      const { questions: pageQuestions, total: pageTotal } = await fetchPage(questions.length);
      setFetchedTotal(pageTotal);
      if (pageQuestions.length > 0) {
        setFetchedQuestions((prev) => [...prev, ...pageQuestions]);
        setIndex((i) => i + 1);
      }
    } catch (e) {
      logEducatorDashboard("ViewQuestionsModal: failed to load more questions", {
        level: LogLevel.WARNING,
        event: "load_more_error",
        statusCode: e?.status,
        standardCode,
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  // `loadedCount` gates render/current; `total` (envelope) drives the counter and
  // the Next bound so the carousel can page through the whole stream.
  const loadedCount = questions.length;
  const current = questions[index];
  const hasQuestions = loadedCount > 0;

  // Names the student rather than using a gendered pronoun.
  const unreconciledSentence = current?.overlay_unreconciled
    ? `${studentName ? `${studentName}'s answer` : "This student's answer"} was recorded as ${current.overlay_correct ? "correct" : "incorrect"}, but it could not be reconciled with this question's options, so no option is marked here.`
    : null;

  // The Student Results variant states these inside the question card: a
  // question not fetched yet, one that cannot be read, and an answer that
  // cannot be reconciled with its verdict.
  const reviewOf = (entry) => ({
    showAnswers,
    optionStates: entry.overlay_option_states,
    answerState: entry.overlay_answer_state,
    withheld: Boolean(entry.overlay_pending || entry.overlay_unavailable),
    notice: entry.overlay_pending
      ? "Loading question\u2026"
      : entry.overlay_unavailable
        ? UNAVAILABLE_QUESTION
        : unreconciledSentence,
  });

  const nav = hasQuestions && (
    <>
      <NavButton aria-label="Previous question" disabled={index === 0 || isLoadingMore} onClick={handlePrev}>
        {studentResults ? <img src={chevronLeftIcon} alt="" width="24" height="24" /> : <FontAwesomeIcon icon={faChevronLeft} />}
      </NavButton>
      <Counter aria-live="polite" aria-atomic="true">
        <div>Question</div>
        <div>{index + 1} of {total}</div>
      </Counter>
      <NavButton aria-label="Next question" disabled={index >= total - 1 || isLoadingMore} onClick={handleNext}>
        {studentResults ? (
          <img src={chevronLeftIcon} alt="" width="24" height="24" style={{ transform: "rotate(180deg)" }} />
        ) : (
          <FontAwesomeIcon icon={faChevronLeft} style={{ transform: "rotate(180deg)" }} />
        )}
      </NavButton>
    </>
  );

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      scrollable
      size={studentResults ? undefined : "lg"}
      dialogAs={studentResults ? StudentResultsDialog : undefined}
      dialogClassName={editable ? "vqm-modal-dialog-edit" : undefined}
      contentClassName="vqm-modal-content"
      aria-label={`Questions for ${standardCode || "standard"}`}
    >
      {/* Fixed header — editable mode is the dark green title band (nav moves into
          the toolbar, under the filters); read-only keeps nav + close up here. */}
      <Modal.Header
        style={editable || studentResults
          ? { background: colors.nightlyWoods500, border: "none", padding: 16 }
          : { border: "none", padding: "32px 32px 24px 32px" }}
      >
        <HeaderBar style={editable || studentResults ? { paddingLeft: 0 } : undefined}>
          {studentResults ? (
            <ModalTitle>Review Student Results</ModalTitle>
          ) : editable ? (
            <ModalTitle>{questionSetId ? "Preview My Question Set" : "Preview & Edit Question Bank"}</ModalTitle>
          ) : (
            <Controls>{nav}</Controls>
          )}
          {isExplicitList && current && !studentResults && (
            <OverlayCaption data-testid="overlay-caption">
              {[standardCode, studentName, current.overlay_label].filter(Boolean).join(" · ")}
            </OverlayCaption>
          )}
          <CloseButton aria-label="Close" onClick={onClose} $onDark={editable || studentResults}>
            {studentResults ? (
              <img src={closeIcon} alt="" width="24" height="24" />
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" stroke={editable ? "#fff" : colors.nightlyWoods950} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </CloseButton>
        </HeaderBar>
      </Modal.Header>

      {/* Date and Group Code follow the shown question, because consecutive
          attempts can come from different sessions. */}
      {studentResults && current && (
        <InfoStrip data-testid="results-info-band">
          <InfoRow style={{ gap: 16 }}>
            <InfoRow style={{ gap: 4 }}>
              <InfoLabel>Student:</InfoLabel>
              <span>{studentName}</span>
            </InfoRow>
            <InfoRow style={{ gap: 4 }}>
              <InfoLabel>Date:</InfoLabel>
              <span>{current.overlay_date}</span>
            </InfoRow>
            {current.overlay_group_code !== null && (
              <InfoRow style={{ gap: 4 }}>
                <InfoLabel>Group Code:</InfoLabel>
                <span>{current.overlay_group_code.toUpperCase()}</span>
              </InfoRow>
            )}
          </InfoRow>
          <InfoRow>
            <InfoLabel>Standard:</InfoLabel>
            <InfoLabel>{standardCode}</InfoLabel>
            <span>{standardName}</span>
          </InfoRow>
        </InfoStrip>
      )}

      {editable && (groupCode || focus) && (
        <InfoStrip>
          {groupCode && (
            <InfoRow style={{ gap: 4 }}>
              <InfoLabel>Group Code:</InfoLabel>
              <span>{groupCode.toUpperCase()}</span>
            </InfoRow>
          )}
          {focus && (
            <InfoRow>
              <InfoLabel>Group Focus:</InfoLabel>
              <TrackTag $meta={trackMeta(focus.trackType)}>{trackMeta(focus.trackType).label}</TrackTag>
              <InfoLabel>{focus.code}</InfoLabel>
              <span>{focus.title}</span>
            </InfoRow>
          )}
        </InfoStrip>
      )}

      {/* Filters and Previous / Next sit outside Modal.Body so they stay pinned
          while only the question below scrolls. */}
      {editable && (
        <EditToolbar>
          {/* Saved set: the set IS the selection, so no filters — its only action
              is the Edit My Question Set footer CTA. */}
          {!questionSetId && (
            <QuestionFilterPanel
              filter={draft}
              onChange={setDraft}
              facets={facetsState.facets}
              hasLoaded={facetsState.hasLoaded}
              error={facetsState.error}
              showStandards={showStandards}
              staticStandardCode={showStandards ? null : lessonStandardCode || standardCode}
              showAutoAssign={false}
              trailingAction={onCreateQuestionSet ? (
                <CreateQuestionSetButton type="button" aria-label="Create question set" onClick={onCreateQuestionSet}>
                  <CirclePlusIcon />
                  Question Set
                </CreateQuestionSetButton>
              ) : null}
            />
          )}
          {!isLoading && !error && hasQuestions && <EditNavRow>{nav}</EditNavRow>}
        </EditToolbar>
      )}

      {/* Scrollable body — question renders top-to-bottom; overflow scrolls here.
          tabIndex + role/label make the scroll region reachable by keyboard
          (the question itself has no focusable controls when previewing). */}
      <Modal.Body
        tabIndex={0}
        role="group"
        aria-label="Question content"
        style={studentResults ? RESULTS_BODY_STYLE : { background: editable ? colors.milkGlass500 : undefined, padding: "0 32px 32px 32px" }}
      >
        {studentResults && hasQuestions && (
          <ResultsControlRow>
            <ResultsControlSide aria-hidden="true" />
            <Controls>{nav}</Controls>
            <ResultsControlSide>
              <ShowAnswersInput
                id={SHOW_ANSWERS_ID}
                type="checkbox"
                checked={showAnswers}
                onChange={(e) => setShowAnswers(e.target.checked)}
              />
              <ShowAnswersLabel htmlFor={SHOW_ANSWERS_ID}>
                <img src={showAnswers ? checkboxCheckedIcon : checkboxUncheckedIcon} alt="" width="24" height="24" />
                Show Answers
              </ShowAnswersLabel>
            </ResultsControlSide>
          </ResultsControlRow>
        )}

        {isLoading && (
          <StateMessage role="status">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p style={{ marginTop: 12 }}>Loading questions…</p>
          </StateMessage>
        )}

        {!isLoading && error && (
          <StateMessage role="alert" style={{ color: "crimson" }}>Error: {error}</StateMessage>
        )}

        {!isLoading && !error && !hasQuestions && (
          <StateMessage>No example questions available{standardName ? ` for ${standardName}` : ""}.</StateMessage>
        )}

        {/* An unreadable question keeps its slot: dropping it would shift
            every later entry and open a different question under the chip. */}
        {!isLoading && !error && hasQuestions && current?.overlay_unavailable && !studentResults && (
          <StateMessage role="status">{UNAVAILABLE_QUESTION}</StateMessage>
        )}

        {/* On rows compiled before the grading fix the stored verdict can
            contradict the recorded option, so no option is marked and the
            verdict is stated instead. */}
        {!isLoading && !error && hasQuestions && current?.overlay_unreconciled && !studentResults && (
          <StateMessage role="status" data-testid="overlay-unreconciled">
            {unreconciledSentence}
          </StateMessage>
        )}

        {!isLoading && !error && hasQuestions && (studentResults || !current?.overlay_unavailable) && (
          <QuestionStage>
            <ConsensusActivityRender
              challenge={buildPreviewChallenge(current)}
              // Explicit-list mode only: this student's own graded answer.
              studentResponses={current.student_responses}
              disableOptions={true}
              isMathJaxSet={false}
              onMCAnswer={() => {}}
              onORAnswer={() => {}}
              showBorder={editable}
              review={studentResults ? reviewOf(current) : undefined}
              showPromptFormula
            />
          </QuestionStage>
        )}
      </Modal.Body>

      {/* Saved set: nothing to save here — the only action is editing the set itself. */}
      {editable && questionSetId && (
        <Modal.Footer style={{ background: colors.milkGlass500, border: "none", gap: "24px", padding: "16px 32px 32px" }}>
          <EditSetButton type="button" onClick={onCreateQuestionSet}>
            <PencilIcon />
            Edit My Question Set
          </EditSetButton>
        </Modal.Footer>
      )}

      {editable && !questionSetId && (
        <Modal.Footer style={{ background: colors.milkGlass500, border: "none", gap: "24px", padding: "16px 32px 32px" }}>
          {saveError && <SaveError role="alert">{saveError}</SaveError>}
          <CancelButton type="button" onClick={onClose} disabled={saving}>
            Cancel
          </CancelButton>
          <SaveButton
            type="button"
            disabled={saving}
            // The loaded total rides along so the host can label the reuse offer's
            // mini card without re-counting the filtered pool.
            onClick={() => onSave && onSave(serializeQuestionFilter(draft), total)}
          >
            {saving ? "Saving…" : "Save"}
          </SaveButton>
        </Modal.Footer>
      )}
    </Modal>
  );
};
